if (!customElements.get('video-carousel-element')) {
  class VideoCarouselElement extends HTMLElement {
    constructor() {
      super();
      this.track = this.querySelector('[data-video-carousel-track]');
      this.slides = Array.from(this.querySelectorAll('[data-video-carousel-slide]'));
      this.prevButton = this.querySelector('[data-video-carousel-prev]');
      this.nextButton = this.querySelector('[data-video-carousel-next]');
      this.viewport = this.querySelector('.video-carousel__viewport');
      this.current = parseInt(this.track?.dataset.initialIndex, 10) || 0;
      this.maxOffset = 2;
      this.mediaQuery = window.matchMedia('(min-width: 1200px)');

      // Gates autoplay so the active video only plays once the carousel is
      // actually scrolled into view (set by the IntersectionObserver below),
      // instead of firing immediately on page load regardless of position.
      this.inView = false;
      this.observer = null;

      this.dragging = false;
      this.dragMoved = false;
      this.dragStartX = 0;
      this.dragStartY = 0;
      this.dragDeltaX = 0;

      this.onPrevClick = this.onPrevClick.bind(this);
      this.onNextClick = this.onNextClick.bind(this);
      this.onSlideClick = this.onSlideClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onBreakpointChange = this.onBreakpointChange.bind(this);
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onIntersect = this.onIntersect.bind(this);
    }

    connectedCallback() {
      if (!this.slides.length) return;

      this.prevButton?.addEventListener('click', this.onPrevClick);
      this.nextButton?.addEventListener('click', this.onNextClick);
      this.track?.addEventListener('click', this.onSlideClick);
      this.addEventListener('keydown', this.onKeydown);
      this.viewport?.addEventListener('pointerdown', this.onPointerDown);

      // Videos are deferred-media: no <video> exists in the DOM until a slide
      // is activated for the first time, so listeners get attached lazily in
      // ensureVideo() rather than up front here.

      if (this.mediaQuery.addEventListener) {
        this.mediaQuery.addEventListener('change', this.onBreakpointChange);
      } else if (this.mediaQuery.addListener) {
        this.mediaQuery.addListener(this.onBreakpointChange);
      }

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(this.onIntersect, { threshold: 0.5 });
        this.observer.observe(this);
      } else {
        // No IntersectionObserver support — fall back to always-in-view so
        // autoplay behaves like before rather than never playing.
        this.inView = true;
      }

      this.updateMaxOffset();
      this.update();
    }

    disconnectedCallback() {
      this.prevButton?.removeEventListener('click', this.onPrevClick);
      this.nextButton?.removeEventListener('click', this.onNextClick);
      this.track?.removeEventListener('click', this.onSlideClick);
      this.removeEventListener('keydown', this.onKeydown);
      this.viewport?.removeEventListener('pointerdown', this.onPointerDown);
      this.viewport?.removeEventListener('pointermove', this.onPointerMove);
      this.viewport?.removeEventListener('pointerup', this.onPointerUp);
      this.viewport?.removeEventListener('pointercancel', this.onPointerUp);
      this.observer?.disconnect();

      if (this.mediaQuery.removeEventListener) {
        this.mediaQuery.removeEventListener('change', this.onBreakpointChange);
      } else if (this.mediaQuery.removeListener) {
        this.mediaQuery.removeListener(this.onBreakpointChange);
      }
    }

    onIntersect(entries) {
      const entry = entries[entries.length - 1];
      this.inView = entry.isIntersecting;
      this.update();
    }

    onBreakpointChange() {
      this.updateMaxOffset();
      this.update();
    }

    updateMaxOffset() {
      this.maxOffset = this.mediaQuery.matches ? 4 : 2;
    }

    onPrevClick() {
      this.goTo(this.current - 1);
    }

    onNextClick() {
      this.goTo(this.current + 1);
    }

    onSlideClick(event) {
      // A drag/swipe just ended on this slide — swallow the trailing click
      // instead of treating it as a tap-to-select.
      if (this.dragMoved) {
        event.preventDefault();
        this.dragMoved = false;
        return;
      }

      const muteButton = event.target.closest('[data-video-carousel-mute]');
      if (muteButton) {
        // Also stops the slide's own <a> wrapper (when the block has a link)
        // from navigating away on this click.
        event.preventDefault();
        this.toggleMute(muteButton);
        return;
      }

      const link = event.target.closest('.video-carousel__link');
      const slide = event.target.closest('[data-video-carousel-slide]');
      if (!slide) return;

      const index = this.slides.indexOf(slide);
      if (index === this.current) return;

      // Let real links navigate; only intercept clicks on non-linked side slides
      // so tapping a peeking video brings it to the center instead of following nowhere.
      if (!link) {
        event.preventDefault();
        this.goTo(index);
      }
    }

    onKeydown(event) {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.goTo(this.current - 1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.goTo(this.current + 1);
      }
    }

    onPointerDown(event) {
      if (event.pointerType === 'mouse' && event.button !== 0) return;

      this.dragging = true;
      this.dragMoved = false;
      this.dragStartX = event.clientX;
      this.dragStartY = event.clientY;
      this.dragDeltaX = 0;

      // Pointer capture is claimed lazily in onPointerMove, only once the
      // gesture is confirmed to be a real drag — grabbing it immediately here
      // would suppress the native click on controls (play/pause, links) for
      // a plain tap, forcing keyboard-only activation.
      this.viewport.addEventListener('pointermove', this.onPointerMove);
      this.viewport.addEventListener('pointerup', this.onPointerUp);
      this.viewport.addEventListener('pointercancel', this.onPointerUp);
    }

    onPointerMove(event) {
      if (!this.dragging) return;

      const deltaX = event.clientX - this.dragStartX;
      const deltaY = event.clientY - this.dragStartY;

      if (!this.dragMoved && Math.abs(deltaX) > 4) {
        this.dragMoved = true;
        this.setAttribute('data-dragging', '');
        this.viewport.setPointerCapture(event.pointerId);
      }

      // Only steal the gesture once it's clearly horizontal, so a vertical
      // swipe can still scroll the page on touch devices.
      if (Math.abs(deltaX) > Math.abs(deltaY)) {
        event.preventDefault();
      }

      this.dragDeltaX = deltaX;
      this.style.setProperty('--drag-x', `${deltaX}px`);
    }

    onPointerUp(event) {
      if (!this.dragging) return;

      this.dragging = false;
      this.removeAttribute('data-dragging');
      this.viewport.removeEventListener('pointermove', this.onPointerMove);
      this.viewport.removeEventListener('pointerup', this.onPointerUp);
      this.viewport.removeEventListener('pointercancel', this.onPointerUp);
      if (this.viewport.hasPointerCapture?.(event.pointerId)) {
        this.viewport.releasePointerCapture(event.pointerId);
      }

      const deltaX = this.dragDeltaX;
      const dragThreshold = 50;
      this.style.setProperty('--drag-x', '0px');
      this.dragDeltaX = 0;

      if (Math.abs(deltaX) > dragThreshold) {
        this.goTo(this.current + (deltaX < 0 ? 1 : -1));
      }
    }

    toggleMute(button) {
      const slide = button.closest('[data-video-carousel-slide]');
      const video = slide?.querySelector('video');
      if (!video) return;

      video.muted = !video.muted;
      button.setAttribute('data-muted', String(video.muted));
      button.setAttribute('aria-pressed', String(video.muted));
      button.setAttribute('aria-label', video.muted ? 'Unmute video' : 'Mute video');
    }

    goTo(index) {
      const total = this.slides.length;
      const wrapped = ((index % total) + total) % total;
      if (wrapped === this.current) return;
      this.current = wrapped;
      this.update();
    }

    // Shortest signed distance from the active slide to `index` around the
    // circular list, so navigating past either end wraps instead of running out.
    getCircularOffset(index) {
      const total = this.slides.length;
      let diff = index - this.current;
      if (diff > total / 2) diff -= total;
      if (diff < -total / 2) diff += total;
      return diff * 2;
    }

    // Videos are deferred-media: nothing exists until the theme's own
    // <video-media> loads it in, normally triggered by clicking its poster
    // button. When `activate` is true (this slide just became centered) we
    // simulate that click so it starts loading itself, exactly as if the
    // viewer had pressed play — after that its real <video> behaves like
    // any other and we control it directly.
    ensureVideo(slide, activate) {
      const videoMedia = slide.querySelector('video-media');
      if (!videoMedia) return null;

      let video = videoMedia.querySelector('video');

      if (!video && activate) {
        const poster = videoMedia.querySelector('.deferred-poster');
        poster?.click();
        video = videoMedia.querySelector('video');

        // Mobile Safari/WebKit sometimes starts the audio track but never
        // paints the first decoded video frame on this very first activation
        // — the exact symptom manually fixed by tapping pause then play on
        // the overlay button. Once real frame data has actually loaded,
        // replay that same pause → play sequence ourselves so the picture
        // catches up instead of staying frozen.
        //
        // Both checks re-confirm this slide is STILL the active one before
        // touching playback — loadeddata (and the setTimeout after it) fire
        // asynchronously, and on a quick swipe through several slides the
        // user may have already moved on by the time either one runs. Without
        // this guard a stale callback would resume a video that isn't
        // supposed to be playing anymore, alongside whichever slide the user
        // has actually landed on.
        video?.addEventListener('loadeddata', () => {
          if (!slide.hasAttribute('data-active') || !this.inView) return;
          video.pause();
          setTimeout(() => {
            if (!slide.hasAttribute('data-active') || !this.inView) return;
            const playPromise = video.play();
            if (playPromise && typeof playPromise.catch === 'function') {
              playPromise.catch(() => {});
            }
          }, 120);
        }, { once: true });
      }

      return video;
    }

    update() {
      this.slides.forEach((slide, index) => {
        const offset = this.getCircularOffset(index);
        const visible = Math.abs(offset) <= this.maxOffset;

        slide.style.setProperty('--offset', offset);
        slide.style.setProperty('--scale', offset === 0 ? 'var(--vc-active-scale)' : '1.3');
        slide.style.opacity = visible ? '1' : '0';
        slide.style.zIndex = String(15 - Math.abs(offset));
        slide.setAttribute('aria-hidden', visible ? 'false' : 'true');
        slide.toggleAttribute('inert', !visible);
        slide.toggleAttribute('data-active', offset === 0);

        const shouldPlay = offset === 0 && this.inView;
        const video = this.ensureVideo(slide, shouldPlay);
        if (video) {
          if (shouldPlay) {
            // The underlying <video> already carries a native `autoplay`
            // attribute (baked into snippets/video.liquid), so the browser
            // starts playing it the instant it's inserted into the DOM —
            // which happens synchronously above, inside ensureVideo(). Calling
            // .play() again here while that's still spinning up races the
            // decoder on mobile (audio keeps going, the picture freezes), so
            // only call it when the video isn't already playing.
            if (video.paused) {
              const playPromise = video.play();
              if (playPromise && typeof playPromise.catch === 'function') {
                playPromise.catch(() => {});
              }
            }
          } else if (!video.paused) {
            video.pause();
          }
        }
      });
    }
  }

  customElements.define('video-carousel-element', VideoCarouselElement);
}
